import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import {
  fetchNationalitiesList,
  selectNationalities,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const DomesticHelperReviewCancellationPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const { handleNextSubstep } = usePolicyCancellationFlow();
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get policy detail from Redux (same selector as travel uses)
  const policyDetailsInfo = useSelector(
    (state) => state.policyDetails?.PolicyDetails?.PolicyHeader
  );
  // Get logged in user profile info
  const profileInfo = useSelector(selectProfileData);

  // Get nationalities from Redux
  const nationalityOptions = useSelector(selectNationalities);

  // Fetch nationalities list if not already loaded
  useEffect(() => {
    if (!nationalityOptions || nationalityOptions.length === 0) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions]);

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== DOMESTIC HELPER POLICY CANCEL DATA DEBUG ===");
  console.log("Full policyDetailsInfo no json:", policyDetailsInfo);
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2)
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("PlanName:", policyDetailsInfo?.PlanName);
  console.log("ProductCode:", policyDetailsInfo?.ProductCode);
  console.log("Premium:", policyDetailsInfo?.Premium);
  console.log("=== END DEBUG ===");

  // Helper function to translate application type
  const translateApplicationType = (applicationType) => {
    if (!applicationType) return translate("N/A", "غير متوفر");

    const typeTranslations = {
      "New": translate("New", "جديد"),
      "Visa Renewal": translate("Visa Renewal", "تجديد التأشيرة"),
      "Temporary": translate("Temporary", "مؤقت"),
      // Also handle lowercase and other variations
      "new": translate("New", "جديد"),
      "visa renewal": translate("Visa Renewal", "تجديد التأشيرة"),
      "temporary": translate("Temporary", "مؤقت"),
      "NEW": translate("New", "جديد"),
      "VISA RENEWAL": translate("Visa Renewal", "تجديد التأشيرة"),
      "TEMPORARY": translate("Temporary", "مؤقت"),
    };

    return typeTranslations[applicationType] || applicationType;
  };

  // Helper function to convert nationality code to name.
  // Robust to inconsistent API data: the value may be a numeric code OR an
  // already-formatted nationality name string depending on the policy.
  const getNationalityName = (nationalityCode) => {
    if (!nationalityCode) return translate("N/A", "غير متوفر");

    // Don't show the raw code while nationalities list is still loading
    if (!nationalityOptions || nationalityOptions.length === 0) return "";

    // Find the nationality by matching code / value / label (case-insensitive)
    const nationality = nationalityOptions.find((nat) => {
      if (typeof nationalityCode === "number") {
        return nat.code === nationalityCode;
      }
      const code = String(nationalityCode).trim().toLowerCase();
      return (
        String(nat.code).toLowerCase() === code ||
        String(nat.value).toLowerCase() === code ||
        String(nat.label).toLowerCase() === code
      );
    });

    if (nationality) {
      return isRTL && nationality.label_ar
        ? nationality.label_ar
        : nationality.label;
    }

    // Not found in the list. If the value is already a name (non-numeric),
    // show it directly instead of N/A. Only fall back to N/A for unmatched codes.
    const isNumericCode = /^\d+$/.test(String(nationalityCode).trim());
    if (!isNumericCode) {
      return String(nationalityCode);
    }
    return translate("N/A", "غير متوفر");
  };

  // Helper function for RTL-aware currency formatting - always use English numerals
  const formatCurrency = (amount) => {
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  // Helper function to format dates as DD/MM/YYYY (same as policy card)
  const formatDateAsNumbers = (dateStr) => {
    if (!dateStr) return "N/A";

    try {
      let date;

      // Handle different date formats
      if (typeof dateStr === "string" && dateStr.includes("/")) {
        // Already in DD/MM/YYYY format
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${
            parts[2]
          }`;
        }
      }

      // Check if it's a unix timestamp (number or string number)
      const timestamp =
        typeof dateStr === "string" ? parseInt(dateStr) : dateStr;
      if (!isNaN(timestamp) && timestamp > 1000000000) {
        // Unix timestamp in seconds, convert to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        // Try parsing as regular date
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) {
        return dateStr; // Return original if can't parse
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr || "N/A";
    }
  };

  // Create domestic helper policy card info (adapting motor structure for domestic helper)
  const policyCardInfo = {
    ModelYear: "",
    Make: "Domestic Helper Policy", // Policy name without plan
    Model: policyDetailsInfo?.Name || "Helper Protection", // Helper name from PolicyHeader.Name
    CPR: "",
    PolicyNumber: policyDetailsInfo?.PolicyNumber || "--",
    RegistrationNumber: "", // Not applicable for domestic helper
    SumInsured: 0.0,
    PlanName: policyDetailsInfo?.PlanName || "--",
    ProductCode: "0222", // Domestic Helper insurance product code
    ProductName: "Domestic Helper Policy", // Updated to match PolicyCard logic
    StartDate:
      policyDetailsInfo?.FromDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    EndDate:
      policyDetailsInfo?.ToDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    FromDateStr: formatDateAsNumbers(policyDetailsInfo?.FromDateStr) || "--",
    ToDateStr: formatDateAsNumbers(policyDetailsInfo?.ToDateStr) || "--",
    Branch: getClientSource(),
    MainChannel: "Direct Bahrain",
    Renewed: policyDetailsInfo?.IsRenewed,
    PolicyStatus: "Active",
    IsRenewable: false,
  };

  /* Next */
  const continueClickHandler = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      isFirstStep={true}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHandler}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHandler}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg pb-32 max767:p-5 max767:mt-0 max767:pt-0 max767:pb-48 mt-6"> 
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your domestic helper policy details before proceeding with cancellation.",
                "قم بمراجعة تفاصيل سياستك وقم بتوفير المعلومات اللازمة للمضي قدمًا في عملية الاستسلام."
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول"
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <PolicyCard
                className="mb-4"
                policy_bg_image={policyCardInfo.policy_bg_image}
                expire_days={policyCardInfo.expire_days}
                is_text_dark={policyCardInfo.is_text_dark}
                mapping_key={policyCardInfo.id}
                data={policyCardInfo}
                disableCardClick
              />

              {/* Domestic Helper specific table - showing required fields */}
              <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                <tbody>
                  {/* 1. Helper Name - From PolicyHeader.Name */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Helper Name", "اسم المساعد")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Name || translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 2. Nationality - From PolicyHeader.Nationality (convert code to name).
                       Fall back across the other nationality fields the API may use. */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Nationality", "الجنسية")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6 lowercase first-letter:uppercase">
                      {getNationalityName(
                        policyDetailsInfo?.Nationality ||
                          policyDetailsInfo?.BeneficiaryNationality ||
                          policyDetailsInfo?.HelperNationality ||
                          policyDetailsInfo?.MaidNationality
                      )}
                    </td>
                  </tr>

                  {/* 3. Date of Birth - From PolicyHeader.DateOfBirthStr (already formatted) */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Date of Birth", "تاريخ الميلاد")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.DateOfBirthStr ||
                        translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 4. Application Type - Translate the value */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Application Type", "نوع الطلب")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {translateApplicationType(
                        policyDetailsInfo?.ApplicationType ||
                        policyDetailsInfo?.PolicyType
                      )}
                    </td>
                  </tr>

                  {/* 5. Working Period - From PolicyHeader.Period (add "year" after value) */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Working Period", "فترة العمل")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Period
                        ? `${policyDetailsInfo.Period} ${
                            Number(policyDetailsInfo.Period) > 1
                              ? translate("years", "سنوات")
                              : translate("year", "سنة")
                          }`
                        : translate("N/A", "غير متوفر")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default DomesticHelperReviewCancellationPage;
